import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppDispatch, useAppSelector } from 'src/store'
import { checkExistsUserTreatment } from 'src/useCase/userTreatment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PATH as PATH_AUTH } from 'src/pages/auth'
import LocalStorage from 'src/utils/LocalStorage'
import useCreateTreatment from 'src/hook/useCreateTreatment'
import { getClinicDetail } from 'src/useCase/clinic'
import setNotificationActions from 'src/store/ui/notification'
import useHandleTreatmentRedirect from 'src/hook/useHandleTreatmentRedirect'

export type FormValues = {
  menu_id: string | number | null
  plan_id?: string | number | null
}

export const useMedicalSpecialtyForm = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const choicesOfUser = useAppSelector(
    (state) => state.domain.choicesUser.choicesUser,
  )
  const from = choicesOfUser?.from || searchParams.get('from') || 'LP'
  const uuid = searchParams.get('uuid')
  const accessToken = LocalStorage.accessToken
  const defaultValues: FormValues = {
    menu_id: null,
    plan_id: null,
  }
  const { createTreatment } = useCreateTreatment()
  const { handleRedirect } = useHandleTreatmentRedirect()
  const [isTelemedicine, setIsTelemedicine] = useState(false)
  const isDifferentClinic = LocalStorage.getDifferentClinicFlag()

  useEffect(() => {
    if (!isDifferentClinic) return

    dispatch(
      setNotificationActions.onNotification({
        type: 'warning',
        message: '選択された店舗（サロン）が前回と異なります。ご確認ください。',
      }),
    )

    LocalStorage.removeDifferentClinicFlag()
  }, [dispatch])

  const validationSchema = Yup.object().shape({
    menu_id: Yup.string().required('入力してください。').nullable(),
  })

  const userTreatmentForm = useForm<FormValues>({
    defaultValues,
  })

  const getTelemedicineStatus = useCallback(async () => {
    if (!uuid) return false
    try {
      const result = await dispatch(getClinicDetail({ uuid })).unwrap()
      const telemedicineEnabled = Boolean(result?.telemedicine_enabled)
      setIsTelemedicine(telemedicineEnabled)
      return telemedicineEnabled
    } catch {
      return false
    }
  }, [uuid, dispatch])

  useEffect(() => {
    getTelemedicineStatus()
  }, [getTelemedicineStatus])

  useEffect(() => {
    if (isTelemedicine) {
      LocalStorage.setTelemedicine('true')
    } else {
      LocalStorage.removeTelemedicine()
    }
  }, [isTelemedicine])

  const onSubmit = async (data: FormValues) => {
    const query = new URLSearchParams({
      uuid: searchParams.get('uuid') || '',
      clinicId: searchParams.get('clinicId') || '',
      menuId: String(data.menu_id || ''),
      from,
      telemedicine: isTelemedicine ? 'true' : 'false',
      gender: LocalStorage.getLocalStorage('selected_gender') || '',
    }).toString()

    if (!accessToken) {
      LocalStorage.setTelemedicine(isTelemedicine ? 'true' : 'false')

      const targetPath = isTelemedicine
        ? PATH_AUTH.REGISTER
        : PATH_AUTH.PRECAUTION_MODAL_ONLINE

      return navigate(`${targetPath}?${query}`)
    }

    try {
      const action = await dispatch(
        checkExistsUserTreatment({ menu_id: data.menu_id! }),
      )

      if (checkExistsUserTreatment.fulfilled.match(action)) {
        const planIdParam = searchParams.get('planId')
        const planId = planIdParam ? Number(planIdParam) : null
        const menuIdNum = data.menu_id ? Number(data.menu_id) : 0

        const newUserTreatment = await createTreatment(
          menuIdNum,
          planId,
          userTreatmentForm,
        )

        if (newUserTreatment) {
          return handleRedirect(newUserTreatment, query)
        }

        return navigate(`${PATH_AUTH.PRECAUTION_MODAL_ONLINE}?${query}`)
      }
    } catch {
      return
    }
  }

  const form = useForm({
    defaultValues,
    shouldUnregister: false,
    shouldFocusError: false,
    mode: 'all',
    resolver: yupResolver(validationSchema),
  })

  return {
    form,
    onSubmit,
  }
}
